// The Lobby's settings (the ⚙ of its header), a sheet from the bottom: the sound effects, the privacy
// policy, signing out. The language and the tutorial stay in the header (owner's rules).
import React, { useState } from "react";
import { ChevronRight, LogOut, ShieldCheck, Volume2 } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "../ui/sheet";
import { Switch } from "../ui/switch";
import { isMuted, setMuted } from "../../lib/feedback";
import { t, useLang } from "../../lib/i18n";

function Row({ icon: Icon, label, children, onClick, testId, tone = "text-slate-200" }) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag
      type={onClick ? "button" : undefined}
      onClick={onClick}
      data-testid={testId}
      className={`flex w-full items-center gap-3 rounded-xl border border-white/[0.06] bg-slate-950/50 px-3.5 py-2.5 text-start text-sm ${tone} ${onClick ? "transition-colors hover:bg-white/[0.05]" : ""}`}
    >
      <Icon className="h-4 w-4 shrink-0 text-amber-300" />
      <span className="flex-1">{label}</span>
      {children}
    </Tag>
  );
}

export function SettingsSheet({ open, onOpenChange, user, onLogout }) {
  const { dir } = useLang();
  const [muted, setMutedState] = useState(isMuted);
  const toggleSound = (on) => {
    setMuted(!on);
    setMutedState(!on);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        dir={dir}
        aria-describedby={undefined}
        data-testid="settings-sheet"
        className="mx-auto max-w-lg rounded-t-3xl border-white/10 bg-slate-900 px-4 pb-8 pt-5 text-white safe-bottom [&>button]:text-slate-400"
      >
        <span aria-hidden="true" className="mx-auto -mt-2 mb-3 block h-1 w-10 rounded-full bg-white/15" />
        <SheetTitle className="mb-3 text-xs uppercase tracking-[0.2em] text-amber-300/90">{t("settings.title")}</SheetTitle>
        <div className="space-y-2">
          <Row icon={Volume2} label={t("settings.sound")}>
            <Switch checked={!muted} onCheckedChange={toggleSound} data-testid="settings-sound" />
          </Row>
          <a href="/privacy" target="_blank" rel="noreferrer" className="block">
            <Row icon={ShieldCheck} label={t("settings.privacy")}>
              <ChevronRight className="h-4 w-4 text-slate-500 rtl:rotate-180" />
            </Row>
          </a>
          <Row icon={LogOut} label={t("settings.logout")} onClick={onLogout} testId="logout-btn" tone="text-rose-200">
            <span className="max-w-[45%] truncate text-xs text-slate-500" dir="auto">
              {user.is_guest ? t("profile.guest") : user.email}
            </span>
          </Row>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default SettingsSheet;
